// Orchestrates the per-story LM graph download that ReaderManager runs at story
// selection (and on the mount/didUpdate story catch-alls): decides WHETHER to
// download (usable story + vosk local-ASR gate), dedups per story, owns the
// progress-UI lifecycle across superseding downloads, and decides when the store
// should be cleared. Extracted from ReaderManager so this decision logic is unit
// tested — the component just forwards progress into setState.

import { shouldDownloadStoryLmGraphs } from '../../CursorOrchestrator';
import {
  clearStoryGraphs,
  ensureStoryGraph,
  getStoryPhraseCount,
  primeStoryGraphs,
} from './voskBiasedGraph';
import { getStoryContentLocaleOrNull as _getStoryContentLocaleOrNull } from '../../playMarkupSequence';
import { withBudget } from '../withBudget';

/**
 * Soft cap on how long story launch waits for the per-story LM graph download.
 * At the deadline the story proceeds; remaining graphs keep downloading in the
 * background and the decode path picks them up mid-story.
 */
export const LM_GRAPH_DOWNLOAD_DEADLINE_MS = 20000;

/**
 * Creates one downloader instance (one per ReaderManager mount).
 *
 * `download(story, { onProgress })` drives the whole lifecycle through the single
 * `onProgress` callback: 0 when a download starts, fractions while it runs, and
 * null when it ends — so the caller can map it straight onto a progress-bar state
 * field. Only the newest download drives the callback; a superseded invocation
 * goes silent instead of overwriting or clearing the newer one's UI.
 *
 * Resolves with the prime summary, or null when nothing was downloaded (unusable
 * story, gate off, or already primed).
 *
 * @param {object} [deps] Injectable for tests.
 * @param {Function} [deps.shouldDownload]
 * @param {Function} [deps.getStoryLocale] Returns the content locale for a story,
 *   or undefined when story is absent/unhydrated. Defaults to a null-safe wrapper
 *   around getStoryContentLocale.
 * @param {Function} [deps.prime]
 * @param {Function} [deps.clear]
 * @param {number} [deps.deadlineMs]
 * @returns {{download: Function, reset: Function}}
 */
export function createStoryLmGraphDownloader({
  shouldDownload = shouldDownloadStoryLmGraphs,
  getStoryLocale = _getStoryContentLocaleOrNull,
  prime = primeStoryGraphs,
  ensure = ensureStoryGraph,
  clear = clearStoryGraphs,
  deadlineMs = LM_GRAPH_DOWNLOAD_DEADLINE_MS,
} = {}) {
  let primedStoryId = null;
  // The pending prime for primedStoryId; null once it settles. Lets a later caller
  // for the SAME story (e.g. an awaited selectStory after a fire-and-forget
  // catch-all claimed it) JOIN the in-flight download instead of resolving early
  // and launching before graphs land.
  let inFlightPrime = null;
  let requestId = 0;

  return {
    async download(story, { onProgress } = {}) {
      const storyId = (story && story.storyId) || null;
      const phraseCount = getStoryPhraseCount(story);
      const report = typeof onProgress === 'function' ? onProgress : () => {};

      if (!storyId || phraseCount === 0 || !shouldDownload({ storyLocale: getStoryLocale(story) })) {
        // Same-story transient (e.g. a mid-hydration story object with a storyId
        // but no phrases yet): KEEP the primed store — clearing here would churn a
        // full re-download when the populated story lands. A different story (or
        // none) clears so graphs never outlive their story.
        if (storyId !== primedStoryId || !storyId) {
          clear();
          primedStoryId = null;
          inFlightPrime = null;
        }
        return null;
      }

      // Every entry path funnels here (awaited selectStory prime, mount/didUpdate
      // catch-alls, redux story updates); prime a story exactly once. A caller for
      // the already-claimed story joins the in-flight prime (so an awaited caller
      // still waits for graphs / the soft deadline embedded in it) or resolves
      // null once it has settled. The joiner's own onProgress is not wired up —
      // the first invocation owns the progress UI.
      if (primedStoryId === storyId) {
        return inFlightPrime;
      }
      primedStoryId = storyId;

      // Only the newest download owns the progress UI: a superseded download's
      // straggling onProgress must not overwrite the new story's bar, and its
      // cleanup must not clear it (primeStoryGraphs also stops stale progress at
      // the source via its prime token).
      requestId += 1;
      const myRequest = requestId;
      const ownsUi = () => requestId === myRequest;

      if (ownsUi()) {
        report(0);
      }
      const primeRun = (async () => {
        try {
          const result = await prime({
            storyId,
            phraseCount,
            softDeadlineMs: deadlineMs,
            onProgress: (fraction) => {
              if (ownsUi()) {
                report(fraction);
              }
            },
          });
          // A COMPLETE failure forgets the story so the next entry path (e.g. the
          // redux story update right after selectStory) retries once. Deliberately
          // narrow: `timedOut` keeps its claim (background workers are still filling
          // the store — a retry would supersede and restart them), partial results
          // keep theirs (a retry would re-download what already loaded), and `stale`
          // means another story owns the store now.
          if (
            result
            && result.ok === false
            && !result.timedOut
            && !result.stale
            && result.loaded === 0
            && primedStoryId === storyId
          ) {
            primedStoryId = null;
          }
          return result;
        } catch (err) {
          // Nothing succeeded; allow a retry on the next entry path.
          if (primedStoryId === storyId) {
            primedStoryId = null;
          }
          throw err;
        } finally {
          if (ownsUi()) {
            report(null);
          }
          if (inFlightPrime === primeRun) {
            inFlightPrime = null;
          }
        }
      })();
      inFlightPrime = primeRun;
      return primeRun;
    },

    /**
     * On-demand re-download of ONE phrase's graph after the up-front prime settled
     * (TT-1283). ReaderManager calls this from the end-of-phrase gap to recover a phrase
     * whose graph the up-front prime gave up on. Gated exactly like `download` (usable
     * story + local-ASR-in-vosk), so it is a no-op on stories/sessions that never use
     * biased graphs. Never touches the progress UI or the primed-story bookkeeping — it
     * writes straight into the existing store via ensureStoryGraph.
     *
     * Bounded by `budgetMs`: the returned promise resolves at the budget even if the
     * download is still running (it keeps going in the background and can still land for
     * a later phrase / a later window). A null/undefined budget awaits the download fully;
     * a `<= 0` budget (deadline already spent) resolves timedOut immediately (see withBudget).
     *
     * @param {object} story
     * @param {number} phraseIndex Story-relative index.
     * @param {{budgetMs?: number}} [opts]
     * @returns {Promise<{ok: boolean, landed: boolean, timedOut?: boolean, stale?: boolean}>}
     */
    async ensurePhrase(story, phraseIndex, { budgetMs } = {}) {
      const storyId = (story && story.storyId) || null;
      if (
        !storyId
        || !Number.isInteger(phraseIndex)
        || phraseIndex < 0
        || phraseIndex >= getStoryPhraseCount(story)
        || !shouldDownload({ storyLocale: getStoryLocale(story) })
      ) {
        return { ok: false, landed: false };
      }

      // The download keeps running in the background if it outruns the budget (so a later
      // decode window can still pick it up); the caller routes this phrase to server for now.
      return withBudget(
        ensure({ storyId, phraseIndex }),
        budgetMs,
        { ok: false, landed: false, timedOut: true },
      );
    },

    /** Drops the store and all downloader state (unmount). */
    reset() {
      requestId += 1;
      primedStoryId = null;
      inFlightPrime = null;
      clear();
    },
  };
}
